import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { deleteAttachments } from 'legacy/lut/services/files/deleteAttachments'
import { uploadAttachments } from 'legacy/lut/services/files/uploadAttachments'
import { useCallback, useState } from 'react'

import { invalidatePartnerAttachmentsCache } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormAttachments } from 'clickNgo/entities/partner/models/form/attachments'
import { resetAllModifiedDataByKey, useGetFormDataField } from 'cng-common/features/form'
import { TForm } from 'cng-common/features/form/models'

import {
  PartnerEditorFormKeys,
  PartnerEditorFormSettings,
  UpdatePartnerResult,
} from '../../../../configs'
import { IPartnerEditorData } from '../../../../models'

interface IUseEditPartnerAttachmentsHandlerParams {
  partnerId: string
}

export const useEditPartnerAttachmentsHandler = ({
  partnerId,
}: IUseEditPartnerAttachmentsHandlerParams) => {
  const [notUploadedAttachments, setNotUploadedAttachments] = useState<string[]>([])
  const dispatch = useAppDispatch()
  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )

  const handleOnPartnerAttachmentsChangesApply = useCallback(
    async ({ modifiedFields, formData, invalidFields }: TForm<IPartnerFormAttachments>) => {
      setNotUploadedAttachments([])

      const hasInvalidModifiedFields = Object.keys(invalidFields).some((invalidField) =>
        modifiedFields.includes(invalidField)
      )

      if (hasInvalidModifiedFields) {
        return UpdatePartnerResult.Failed
      }

      const attachmentsForDelete = actualData.partnerAttachments.filter((attachment) =>
        formData.attachments.every(
          (actualAttachment) => actualAttachment.entryId !== attachment.entryId
        )
      )

      const attachmentsForUpdate = formData.attachments.filter((attachment) =>
        actualData.partnerAttachments.every(
          (actualAttachment) => actualAttachment.entryId !== attachment.entryId
        )
      )

      const oldAttachments = actualData.partnerAttachments.filter((attachment) =>
        formData.attachments.some(
          (actualAttachment) => actualAttachment.entryId === attachment.entryId
        )
      )

      try {
        await deleteAttachments(attachmentsForDelete)

        const newAttachments = await uploadAttachments({
          attachments: attachmentsForUpdate,
          partnerId,
        })

        updateActualData({
          ...actualData,
          partnerAttachments: [...oldAttachments, ...newAttachments],
        })

        dispatch(resetAllModifiedDataByKey(PartnerEditorFormKeys.Attachments))

        if (newAttachments.length !== attachmentsForUpdate.length) {
          setNotUploadedAttachments(() =>
            attachmentsForUpdate
              .filter((attachment) => {
                return !newAttachments.some(
                  (newAttachment) => newAttachment.entryId === attachment.entryId
                )
              })
              .map((attachment) => attachment.fileName)
          )

          return UpdatePartnerResult.Failed
        }

        return UpdatePartnerResult.Updated
      } catch (error) {
        return UpdatePartnerResult.Failed
      } finally {
        invalidatePartnerAttachmentsCache(partnerId)
      }
    },
    [actualData, dispatch, partnerId, updateActualData]
  ) as unknown as (formData: TForm<IPartner>) => Promise<UpdatePartnerResult>

  const handleOnPartnerAttachmentsChangesCancel = useCallback(
    ({ modifiedFields }: TForm<IPartnerFormAttachments>) => {
      if (modifiedFields.length) {
        dispatch(resetAllModifiedDataByKey(PartnerEditorFormKeys.Attachments))
      }

      setNotUploadedAttachments([])
    },
    [dispatch]
  ) as unknown as (formData: TForm<IPartner>) => void

  return {
    handleOnPartnerAttachmentsChangesApply,
    handleOnPartnerAttachmentsChangesCancel,
    notUploadedAttachments,
  }
}
